import React, { useState, useCallback, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useSelector } from "react-redux";
import PersistentDialog from "../../components/common/PersistentDialog";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Tooltip,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search as SearchIcon,
  Visibility,
  Close,
  Download,
  Upload,
  School,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Description,
  FileDownload,
  AccessTime,
} from "@mui/icons-material";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useQueryClient } from '@tanstack/react-query';
import courseService from "../../services/courseService";
import importService from "../../services/importService";
import api from "../../services/api";
import { DataGrid } from "@mui/x-data-grid";
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText,
  InputAdornment,
  Switch
} from '@mui/material';
import DataImportWizard from "../../components/common/DataImportWizard";
import PermissionGate from "../../components/common/PermissionGate";
import DepartmentModuleFilterSelect from "../../components/common/DepartmentModuleFilterSelect";
import { useDepartmentModuleFilter } from "../../hooks/useDepartmentModuleFilter";
import StatusDropdown from "../../components/common/StatusDropdown";

const Courses = () => {
  const queryClient = useQueryClient();
  const { selectedInstitute } = useSelector((state) => state.instituteContext || {});
  const {
    departmentFilter,
    setDepartmentFilter,
    showAllDepartmentsOption,
    departmentApiParams,
    effectiveInstituteId,
  } = useDepartmentModuleFilter();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  // Initialize with default sort and pagination
  const [sortModel, setSortModel] = useState([
    {
      field: 'createdAt',
      sort: 'desc',
    },
  ]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [viewingCourse, setViewingCourse] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [optimisticStatus, setOptimisticStatus] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    duration: "",
    description: "",
    departmentId: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [submittingCourse, setSubmittingCourse] = useState(false);

  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });

  // Import states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);

  const validateForm = () => {
    const errors = {};
    
    // Course name validation
    const nameTrimmed = formData.name.trim();
    if (!nameTrimmed) {
      errors.name = 'Course name is required';
    } else if (nameTrimmed.length < 3) {
      errors.name = 'Course name must be at least 3 characters long';
    } else if (nameTrimmed.length > 50) {
      errors.name = 'Course name cannot exceed 50 characters';
    } else if (/\d/.test(nameTrimmed)) {
      errors.name = 'Course name cannot contain numbers';
    }
    
    // Other validations
    const codeTrimmed = formData.code.trim();
    if (!codeTrimmed) {
      errors.code = 'Course code is required';
    } else if (codeTrimmed.length > 50) {
      errors.code = 'Course code cannot exceed 50 characters';
    }
    if (!formData.duration) errors.duration = 'Duration is required';
    if (!formData.departmentId) errors.departmentId = 'Department is required';
    const descTrimmed = formData.description?.trim() || '';
    if (descTrimmed && descTrimmed.length < 3) {
      errors.description = 'Description must be at least 3 characters long';
    } else if (descTrimmed.length > 100) {
      errors.description = 'Description cannot exceed 100 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };


  // Set initial state when component mounts
  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setSortModel([
      {
        field: 'createdAt',
        sort: 'desc',
      },
    ]);
    setSearchQuery('');
  }, []);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [departmentFilter, statusFilter]);

  // Fetch departments for the dropdown with error handling and logging
  const { data: departmentsResponse = { data: [] }, isLoading: isDeptLoading, error: deptError } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        console.log('Fetching departments...');
        const response = await api.get('/institute/get-departments-names');
        console.log('Departments API Response:', response);
        // The API returns { status, message, data: [...] }
        return response.data;
      } catch (error) {
        console.error('Error fetching departments:', error);
        // Show error to user
        setSnackbar({
          open: true,
          message: 'Failed to load departments',
          severity: 'error'
        });
        return { data: [] };
      }
    },
    // Optional: Add retry logic
    retry: 2,
    refetchOnWindowFocus: false
  });

  // Extract departments array from the response
  const departments = Array.isArray(departmentsResponse?.data) ? departmentsResponse.data : [];

  // Log any errors
  if (deptError) {
    console.error('Departments query error:', deptError);
  }

  // Fetch courses with pagination, sorting, and search
  const {
    data: apiResponse,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery({
    queryKey: [
      'courses',
      paginationModel.page,
      paginationModel.pageSize,
      sortModel[0]?.field || 'createdAt',
      sortModel[0]?.sort || 'desc',
      searchQuery,
      statusFilter,
      departmentApiParams.departmentId ?? null,
      effectiveInstituteId,
    ],
    queryFn: async () => {
      const params = {
        page: paginationModel.page + 1, // API is 1-indexed, MUI is 0-indexed
        limit: paginationModel.pageSize,
        sort: sortModel[0]?.field || 'createdAt',
        order: sortModel[0]?.sort || 'desc',
        search: searchQuery,
      };
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const instId = currentUser?.instituteId ?? selectedInstitute?.id;
      if (instId) params.instituteId = instId;
      if (departmentApiParams.departmentId) {
        params.departmentId = departmentApiParams.departmentId;
      }
      console.log('Fetching courses with params:', params);
      const response = await courseService.getCourses(params);
      console.log('Courses API Response:', response);
      return response?.data || { data: { courses: [], pagination: { total: 0 } } };
    },
    // Ensure the query runs when the component mounts and refetches on mount
    enabled: true,
    // Always refetch when the component mounts
    refetchOnMount: true,
    // Don't use stale data, always fetch fresh data
    staleTime: 0,
    // Keep previous data while fetching new data for smoother UX
    keepPreviousData: true,
  });

  // Normalize API response to extract rows and counts reliably
  const _root = apiResponse?.data ?? apiResponse ?? {};
  const coursesRaw = (
    Array.isArray(_root?.courses) ? _root.courses :
    Array.isArray(_root?.data?.courses) ? _root.data.courses :
    Array.isArray(_root?.data) ? _root.data :
    Array.isArray(_root?.rows) ? _root.rows :
    Array.isArray(_root?.items) ? _root.items :
    Array.isArray(_root?.result) ? _root.result :
    Array.isArray(_root?.content) ? _root.content :
    Array.isArray(_root?.data?.data) ? _root.data.data :
    Array.isArray(_root?.data?.rows) ? _root.data.rows :
    []
  );

  const rowCount = (
    Number(_root?.pagination?.total) ||
    Number(_root?.pagination?.totalItems) ||
    Number(_root?.meta?.totalItems) ||
    Number(_root?.total) ||
    Number(_root?.count) ||
    (Array.isArray(coursesRaw) ? coursesRaw.length : 0)
  ) || 0;

  // Map raw courses to DataGrid rows
  const rows = coursesRaw.map((c, index) => {
    const deptEntity = c?.Department || c?.department || c?.departmentDetails || c?.department_info || null;
    const departmentName = (typeof deptEntity === 'string')
      ? deptEntity
      : (deptEntity?.name || c?.departmentName || c?.department_name || 'N/A');

    const id = c?.id ?? c?.courseId ?? c?._id ?? `${c?.code || c?.name || 'course'}-${index}`;
    const name = c?.name ?? c?.courseName ?? c?.title ?? '';
    const code = c?.code ?? c?.courseCode ?? '';
    const duration = c?.duration ?? c?.durationYears ?? c?.years ?? '';
    const students = c?.studentsCount ?? c?.students_count ?? c?.studentCount ?? 0;
    const subjectsCount = c?.subjectsCount ?? c?.subjects_count ?? c?.subjectCount ?? 0;
    const status = (typeof c?.status === 'boolean')
      ? c.status
      : (typeof c?.status === 'string')
        ? (c.status?.toLowerCase() === 'active' || c.status?.toLowerCase() === 'true')
        : (c?.isActive === true || c?.is_active === true || c?.active === true);

    return {
      id,
      name,
      code,
      description: c?.description ?? c?.details ?? '',
      departmentId: c?.departmentId ?? c?.department_id ?? deptEntity?.id ?? '',
      department: departmentName,
      Department: deptEntity || null,
      duration,
      students,
      subjectsCount,
      status: !!status,
      canDelete: c?.canDelete !== false,
    };
  });

  // Statistics from response with safe fallbacks
  const totalCourses = _root?.totalCourses ?? _root?.data?.totalCourses ?? rowCount;
  const activeCourses = _root?.activeCourses ?? _root?.data?.activeCourses ?? rows.filter(r => r.status).length;
  const totalStudents = _root?.totalStudents ?? _root?.data?.totalStudents ?? rows.reduce((a, r) => a + (r.students || 0), 0);
  const totalSubjects = _root?.totalSubjects ?? _root?.data?.totalSubjects ?? rows.reduce((a, r) => a + (r.subjectsCount || 0), 0);

  const handleSortChange = (field) => {
    setSortModel(prevSortModel => {
      // If clicking the same field, toggle the sort direction
      if (prevSortModel[0]?.field === field) {
        return [{
          field,
          sort: prevSortModel[0]?.sort === 'asc' ? 'desc' : 'asc'
        }];
      }
      // If clicking a different field, sort by that field in descending order by default
      return [{
        field,
        sort: 'desc'
      }];
    });
  };

  // This effect ensures the query refetches when sort model changes
  useEffect(() => {
    // The useQuery will automatically refetch when sortModel changes
    // because it's part of the queryKey
  }, [sortModel]);

  const importSteps = ["Select File", "Preview & Validate", "Import"];

  const samplePreviewData = [
    {
      name: "B.Sc Computer Science",
      code: "BSCS",
      duration: "3 years",
      department: "Computer Science",
      status: "valid",
    },
    {
      name: "B.Tech AI & ML",
      code: "BTAIML",
      duration: "4 years",
      department: "Computer Science",
      status: "valid",
    },
    {
      name: "M.Sc Data Science",
      code: "",
      duration: "2 years",
      department: "Computer Science",
      status: "error",
      error: "Course code is required",
    },
    {
      name: "Diploma in Web Dev",
      code: "DWD",
      duration: "1 year",
      department: "Computer Science",
      status: "valid",
    },
  ];

  const handleDownloadTemplate = async () => {
    try {
      const response = await importService.downloadCourseTemplate();
      const top = response?.data;
      const inner = top?.data && typeof top.data === "object" ? top.data : null;
      const body = inner && typeof inner.csvData === "string" ? inner : top;
      const csv = typeof body?.csvData === "string" ? body.csvData : null;
      if (!csv) throw new Error("no csv");
      const rawName = body?.fileName && String(body.fileName).trim() ? String(body.fileName).trim() : "courses_import_template.csv";
      const fileName = /\.csv$/i.test(rawName) ? rawName : `${rawName.replace(/\.[^.]+$/i, "")}.csv`;
      const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSnackbar({
        open: true,
        message: 'Template downloaded successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error downloading template:', error);
      setSnackbar({
        open: true,
        message: 'Failed to download template',
        severity: 'error',
      });
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewData(samplePreviewData);
      setImportStep(1);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setImporting(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await courseService.importCourses(formData);

      setImportStep(2);
      setSnackbar({
        open: true,
        message: 'Courses imported successfully',
        severity: 'success',
      });

      // Refresh the courses list
      queryClient.invalidateQueries('courses');

    } catch (error) {
      console.error('Error importing courses:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to import courses',
        severity: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  const resetImportDialog = () => {
    setImportStep(0);
    setSelectedFile(null);
    setPreviewData([]);
    setImporting(false);
    setImportDialogOpen(false);
  };

  const handleEditCourse = async (course) => {
    setIsEditMode(true);
    setEditingCourseId(course.id);
    setFormData({
      name: course.name || "",
      code: course.code || "",
      duration: course.duration ? String(course.duration) : "",
      description: course.description || "",
      departmentId: course.departmentId || ""
    });
    setCreateDialogOpen(true);
  };

  // Toggle course status
  const toggleCourseStatus = useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await api.put(`/course/status-update?id=${id}`, { status });
      return response.data;
    },
    onSuccess: (data, variables) => {
      const { id, status } = variables;
      try {
        queryClient.setQueriesData(
          { queryKey: ['courses'], exact: false },
          (old) => {
            if (old == null || typeof old !== 'object') return old;
            const root = old.data ?? old;
            const list = Array.isArray(root?.courses) ? root.courses
              : Array.isArray(root?.data?.courses) ? root.data.courses
              : Array.isArray(root?.data) ? root.data
              : Array.isArray(root?.rows) ? root.rows
              : Array.isArray(root?.items) ? root.items
              : null;
            if (!Array.isArray(list)) return old;
            const nextList = list.map((c) => {
              const cid = c?.id ?? c?.courseId ?? c?._id;
              if (cid == null) return c;
              if (String(cid) === String(id)) return { ...c, status };
              return c;
            });
            if (Array.isArray(root?.courses)) return { ...old, data: { ...root, courses: nextList } };
            if (root?.data?.courses) return { ...old, data: { ...root, data: { ...root.data, courses: nextList } } };
            if (Array.isArray(root?.data)) return { ...old, data: { ...root, data: nextList } };
            return old;
          }
        );
      } catch (e) {
        // ignore cache update errors
      }
      queryClient.invalidateQueries({ queryKey: ['courses'] });
      setSnackbar({
        open: true,
        message: 'Course status updated successfully',
        severity: 'success'
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to update course status',
        severity: 'error'
      });
    }
  });

  const handleStatusToggle = (course) => {
    if (course.canDelete === false) return;
    const id = course?.id;
    if (id == null) return;

    const newStatus = !course.status;
    setUpdatingStatus(id);
    setOptimisticStatus((prev) => ({ ...prev, [id]: newStatus }));

    toggleCourseStatus.mutate(
      { id, status: newStatus },
      {
        onError: () => {
          setOptimisticStatus((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        },
        onSettled: () => {
          setUpdatingStatus(null);
          setOptimisticStatus((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        },
      }
    );
  };

  const handleViewCourse = (course) => {
    setViewingCourse(course);
    setViewDialogOpen(true);
  };

  // Handle delete confirmation dialog open
  const handleDeleteClick = (courseId) => {
    setCourseToDelete(courseId);
    setDeleteDialogOpen(true);
  };

  // Handle course deletion
  const handleDeleteCourse = async () => {
    if (!courseToDelete) return;
    
    try {
      await api.delete(`/course/delete?id=${courseToDelete}`);

      // Invalidate and refetch courses to update the list
      queryClient.invalidateQueries({ queryKey: ['courses'] });

      setSnackbar({
        open: true,
        message: 'Course deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting course:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete course',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
    }
  };

  // Handle dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setCourseToDelete(null);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      code: "",
      duration: "",
      description: "",
      departmentId: ""
    });
    setIsEditMode(false);
    setEditingCourseId(null);
    setViewingCourse(null);
  };

  const handleSubmitCourse = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmittingCourse(true);
    try {
      const courseData = {
        name: formData.name,
        code: formData.code,
        duration: formData.duration,
        description: formData.description,
        departmentId: formData.departmentId
      };

      console.log(isEditMode ? 'Updating course with data:' : 'Creating course with data:', courseData);
      
      let response;
      if (isEditMode && editingCourseId) {
        // Update existing course
        response = await api.put(`/course/update?id=${editingCourseId}`, courseData);
      } else {
        // Create new course
        response = await api.post('/course/create', courseData);
      }

      console.log('Course operation response:', response);
      
      setSnackbar({
        open: true,
        message: response.data?.message || 
                (isEditMode ? 'Course updated successfully!' : 'Course created successfully!'),
        severity: 'success'
      });
      
      // Reset form and close dialog
      resetForm();
      
      // Refresh the courses list
      queryClient.invalidateQueries('courses');
      setCreateDialogOpen(false);
      
    } catch (error) {
      console.error('Error in course operation:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 
                `Failed to ${isEditMode ? 'update' : 'create'} course. Please try again.`,
        severity: 'error'
      });
    } finally {
      setSubmittingCourse(false);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Course",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const courseName = params.row.name || "-";
        const courseCode = params.row.code || "";
        return (
          <Tooltip title={courseName} placement="top-start" enterDelay={400}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
              <Avatar sx={{ bgcolor: "primary.light", width: 36, height: 36, flexShrink: 0 }}>
                <School />
              </Avatar>
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography variant="body2" fontWeight={500} noWrap>
                  {courseName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {courseCode}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        );
      },
    },
    { field: "department", headerName: "Department", width: 250 },
    { field: "duration", headerName: "Duration", width: 100 },
    { field: "students", headerName: "Students", width: 100 },
    { 
      field: "subjectsCount", 
      headerName: "Subjects", 
      width: 100,
      valueGetter: (params) => params.row.subjectsCount || 0,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.subjectsCount || 0}
        </Typography>
      )
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const rowId = params.row?.id;
        if (rowId == null) return null;
        const isActive = optimisticStatus[rowId] ?? !!params.value;
        const statusChip = (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            size="small"
            color={isActive ? 'success' : 'default'}
          />
        );
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} key={rowId}>
            {/* Changing status is an update operation: only show the toggle with course.update. */}
            <PermissionGate permission="course.update" fallback={statusChip}>
              <Switch
                key={rowId}
                checked={!!isActive}
                onChange={() => handleStatusToggle(params.row)}
                color="success"
                size="small"
                disabled={updatingStatus === rowId}
              />
              {statusChip}
            </PermissionGate>
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={() => handleViewCourse(params.row)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <PermissionGate permission="course.update">
            <Tooltip title="Edit">
              <IconButton 
                size="small"
                onClick={() => handleEditCourse(params.row)}
                color="primary"
              >
                <Edit />
              </IconButton>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission="course.delete">
            <Tooltip title={params.row.canDelete === false ? "Delete disabled" : "Delete"}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={params.row.canDelete === false}
                  onClick={() => params.row.canDelete !== false && handleDeleteClick(params.row.id)}
                >
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
          </PermissionGate>
        </Box>
      ),
    },
  ];

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle export functionality (same format as subject export)
  const handleExport = async () => {
    try {
      const path = "/export/courses-csv";
      const response = await api.post(path);

      if (response.status !== 200) {
        throw new Error('Export failed');
      }

      // Handle CSV data from response
      const csvData = response.data.csvData;
      if (!csvData) {
        throw new Error('No CSV data received');
      }

      // Create blob from CSV string
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const fileUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = fileUrl;
      a.download = 'courses.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(fileUrl);
      document.body.removeChild(a);

      // Show success message
      setSnackbar({
        open: true,
        message: `Export successful! ${response.data.totalRecords || 0} courses exported.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Export error:", error);
      setSnackbar({
        open: true,
        message: "Failed to export courses. Please try again.",
        severity: "error",
      });
    }
  };

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  // Statistics from the API response
  const stats = [
    {
      label: 'Total Courses',
      value: totalCourses,
      color: 'primary.main',
    },
    {
      label: 'Active Courses',
      value: activeCourses,
      color: 'success.main',
    },
    {
      label: 'Total Students',
      value: totalStudents,
      color: 'info.main',
    },
    {
      label: 'Total Subjects',
      value: totalSubjects,
      color: 'warning.main',
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with title and action buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={600}>
            Course Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <PermissionGate permission="course.import">
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => setImportDialogOpen(true)}
              >
                Import
              </Button>
            </PermissionGate>
            <PermissionGate permission="course.export">
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleExport}
                disabled={rows.length === 0}
              >
                Export
              </Button>
            </PermissionGate>
            <PermissionGate permission="course.create">
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Add Course
              </Button>
            </PermissionGate>
          </Box>
        </Box>
        
        {/* Search bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                sx: { 
                  backgroundColor: 'background.paper',
                  minWidth: 300,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                },
              }}
            />
            <StatusDropdown
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              label="Status"
              size="small"
              sx={{ minWidth: 160 }}
            />
            <DepartmentModuleFilterSelect
              labelId="courses-department-filter"
              value={departmentFilter}
              onChange={setDepartmentFilter}
              instituteId={effectiveInstituteId}
              showAllOption={showAllDepartmentsOption}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total: {rowCount} courses
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: stat.color }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        </Grid>

        <Card>
          <CardContent>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={isLoading || isFetching}
              autoHeight
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={(model) =>
                setPaginationModel(prev => (
                  model.pageSize !== prev.pageSize ? { ...model, page: 0 } : model
                ))
              }
              rowCount={rowCount}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              disableRowSelectionOnClick
              sx={{ border: 'none' }}
            />
          </CardContent>
        </Card>
      </Box>

      {/* Use shared DataImportWizard for course imports so template download and required-fields display are consistent */}
      <DataImportWizard
        open={importDialogOpen}
        onClose={resetImportDialog}
        entityType="courses"
        instituteId={currentUser?.instituteId}
        onImportComplete={() => {
          // Keep wizard open so success/details screen is visible (same as class import).
          queryClient.invalidateQueries(['courses']);
        }}
      />

      {/* Create Course Dialog */}
      <PersistentDialog 
        open={createDialogOpen} 
        onClose={() => {
          setCreateDialogOpen(false);
          resetForm();
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>{isEditMode ? 'Edit Course' : 'Add New Course'}</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmitCourse}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Course Name *"
                  value={formData.name}
                  onChange={(e) => {
                    // Prevent numeric input
                    const value = e.target.value.replace(/[0-9]/g, '');
                    setFormData({ ...formData, name: value });
                  }}
                  inputProps={{
                    minLength: 3,
                    maxLength: 50,
                    'data-testid': 'course-name-input'
                  }}
                  error={!!formErrors.name}
                  helperText={formErrors.name || '3-50 characters, no numbers allowed'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Course Code *"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  inputProps={{
                    minLength: 1,
                    maxLength: 50,
                    'data-testid': 'course-code-input'
                  }}
                  error={!!formErrors.code}
                  helperText={formErrors.code || '1-50 characters'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Duration (in years) *"
                  value={formData.duration}
                  onChange={(e) => {
                    // Allow only numbers and limit to 2 digits
                    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
                    setFormData({ ...formData, duration: value });
                  }}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  error={!!formErrors.duration}
                  helperText={formErrors.duration || 'Enter number of years (max 2 digits)'}
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: 2,
                    'data-testid': 'duration-input'
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">years</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!formErrors.departmentId}>
                  <InputLabel id="department-select-label">Department *</InputLabel>
                  <Select
                    labelId="department-select-label"
                    value={formData.departmentId || ''}
                    label="Department *"
                    onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                    disabled={isDeptLoading}
                  >
                    {isDeptLoading ? (
                      <MenuItem disabled>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading departments...
                        </Box>
                      </MenuItem>
                    ) : departments.length === 0 ? (
                      <MenuItem disabled>No departments found</MenuItem>
                    ) : (
                      departments.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {formErrors.departmentId ? (
                    <FormHelperText>{formErrors.departmentId}</FormHelperText>
                  ) : deptError ? (
                    <FormHelperText error>Error loading departments</FormHelperText>
                  ) : null}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  inputProps={{
                    minLength: 3,
                    maxLength: 100,
                    'data-testid': 'course-description-input'
                  }}
                  error={!!formErrors.description}
                  helperText={formErrors.description || '3-100 characters (optional)'}
                />
              </Grid>
            </Grid>
            <DialogActions sx={{ mt: 2 }}>
              <Button 
                onClick={() => {
                  setCreateDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={isDeptLoading || submittingCourse}
                startIcon={submittingCourse ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {submittingCourse ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Course' : 'Create Course')}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </PersistentDialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleDeleteDialogClose();
        }}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Course
        </DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this course? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteCourse} color="error" variant="contained" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* View Course Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setViewDialogOpen(false);
        }}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Course Details</DialogTitle>
        <DialogContent>
          {viewingCourse && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Course Name</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCourse.name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Course Code</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCourse.code}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Duration</Typography>
                  <Typography variant="body1" gutterBottom>{viewingCourse.duration} years</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Department</Typography>
                  <Typography variant="body1" gutterBottom>
                    {viewingCourse.Department?.name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1" gutterBottom sx={{ whiteSpace: 'pre-line' }}>
                    {viewingCourse.description || 'No description available'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={viewingCourse.status ? 'Active' : 'Inactive'} 
                    color={viewingCourse.status ? 'success' : 'default'} 
                    size="small" 
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Courses;
